import { OAuth2ErrorCode } from "./oAuthTypes";

import type { ErrorMapping } from "@/errors";
import { ErrorSeverity } from "@/errors";

export const mapHTTPStatusToOAuthError = (status: number): OAuth2ErrorCode => {
  switch (status) {
    case 400:
      return "invalid_request";
    case 401:
      return "invalid_token";
    case 403:
      return "access_denied";
    case 429:
      return "network_error";
    case 500:
      return "server_error";
    case 503:
      return "temporarily_unavailable";
  }
  return "unknown";
};

export const OAUTH_ERROR_MAPPINGS: Record<OAuth2ErrorCode, ErrorMapping> = {
  invalid_client: {
    userMessage: "Authentication configuration error. Please contact support.",
    internalMessage: "OAuth client credentials invalid",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  invalid_grant: {
    userMessage: "Your session has expired. Please sign in again.",
    internalMessage: "OAuth refresh token expired or revoked",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  access_denied: {
    userMessage: "Access was denied. Please grant permission to continue.",
    internalMessage: "User denied OAuth permission",
    shouldRetry: false,
    severity: ErrorSeverity.Info,
  },
  invalid_token: {
    userMessage: "Your authentication token is invalid. Please sign in again.",
    internalMessage: "OAuth access token invalid",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  expired_token: {
    userMessage: "Your session has expired. Please sign in again.",
    internalMessage: "OAuth access token expired",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  redirect_uri_mismatch: {
    userMessage: "Authentication configuration error. Please contact support.",
    internalMessage: "OAuth redirect URI mismatch",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  insufficient_scope: {
    userMessage: "Additional permissions are required. Please sign in again.",
    internalMessage: "OAuth insufficient scope",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  network_error: {
    userMessage: "Network error. Please check your connection and try again.",
    internalMessage: "OAuth network error",
    shouldRetry: true,
    severity: ErrorSeverity.Warning,
  },
  timeout_error: {
    userMessage: "Request timed out. Please try again.",
    internalMessage: "OAuth request timeout",
    shouldRetry: true,
    severity: ErrorSeverity.Warning,
  },
  invalid_request: {
    userMessage: "Invalid request. Please try again.",
    internalMessage: "OAuth invalid request parameters",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  unsupported_grant_type: {
    userMessage: "Authentication method not supported. Please contact support.",
    internalMessage: "OAuth unsupported grant type",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  invalid_scope: {
    userMessage: "Invalid permissions requested. Please contact support.",
    internalMessage: "OAuth invalid scope",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  unauthorized_client: {
    userMessage: "Application not authorized. Please contact support.",
    internalMessage: "OAuth client not authorized",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  unknown: {
    userMessage: "An unexpected error occurred. Please try again.",
    internalMessage: "Unknown OAuth error",
    shouldRetry: true,
    severity: ErrorSeverity.Error,
  },
  server_error: {
    userMessage: "An unexpected error occurred. Please try again.",
    internalMessage: "OAuth server error",
    shouldRetry: true,
    severity: ErrorSeverity.Error,
  },
  temporarily_unavailable: {
    userMessage:
      "Service temporarily unavailable. Please try again in a moment.",
    internalMessage: "OAuth service temporarily unavailable",
    shouldRetry: true,
    severity: ErrorSeverity.Warning,
  },
};
